import { useCallback, useEffect, useState } from 'react';

import { DEFAULT_MAIL_HISTORY_FILTER } from 'app/modules/mail-history/core/constants';
import { MailHistoryFilterParams } from 'app/modules/mail-history/core/types';
import { useChangeParams } from 'app/modules/query-state/hooks';
import { useQueryParamStore } from 'app/modules/query-state/store';

import isEqual from 'lodash/isEqual';

const useMailHistoryFilter = (
  openFilter: boolean,
  setIsOpenFilter: React.Dispatch<
    React.SetStateAction<{
      content: boolean;
      recipient: boolean;
      filter: boolean;
    }>
  >,
) => {
  const onChangeParams = useChangeParams().onChangeParams;
  const curParams = useQueryParamStore((s) => s.state.curParams);

  const [filter, setFilter] = useState<MailHistoryFilterParams>(DEFAULT_MAIL_HISTORY_FILTER);
  const [menuIsOpen, setMenuIsOpen] = useState<{
    organization: boolean;
    recipientType: boolean;
    type: boolean;
  }>({
    organization: false,
    recipientType: false,
    type: false,
  });

  useEffect(() => {
    if (!openFilter || isEqual(curParams, filter)) return;
    setFilter({
      organizationId: Array.isArray(curParams.organizationId) ? curParams.organizationId : [],
      recipientType: Array.isArray(curParams.recipientType) ? curParams.recipientType : [],
      type: Array.isArray(curParams.type) ? curParams.type : [],
    });
  }, [curParams, openFilter]);

  const handleCloseFilter = () => {
    setIsOpenFilter({
      content: false,
      recipient: false,
      filter: false,
    });
  };

  const handleApplyFilter = () => {
    onChangeParams({
      ...filter,
      page: 1,
    });
    handleCloseFilter();
  };

  const handleClearAll = () => {
    setFilter(DEFAULT_MAIL_HISTORY_FILTER);
    onChangeParams(DEFAULT_MAIL_HISTORY_FILTER);
    handleCloseFilter();
  };

  const handleSelectChange = useCallback(
    (key: keyof MailHistoryFilterParams) => (newValue: { value: string }[]) => {
      setFilter((prev) => ({
        ...prev,
        [key]: newValue.map((el) => String(el.value)),
      }));
      setMenuIsOpen((prev) => ({
        ...prev,
        [key === 'organizationId' ? 'organization' : key]: true,
      }));
    },
    [],
  );

  const handleMenuClose = (key: keyof typeof menuIsOpen) => () =>
    setMenuIsOpen({ ...menuIsOpen, [key]: false });

  const handleMenuOpen = (key: keyof typeof menuIsOpen) => () =>
    setMenuIsOpen({ ...menuIsOpen, [key]: true });

  return {
    filter,
    menuIsOpen,

    handleClearAll,
    handleMenuOpen,
    handleMenuClose,
    handleSelectChange,
    handleApplyFilter,
  };
};

export default useMailHistoryFilter;
